import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { customerAPI, measurementAPI } from '../../services/api';
import MeasurementModal from '../SalesOrders/MeasurementModal';
import { useNotification } from '../../hooks/useNotification';
import { t } from 'i18next';

const CustomerList = () => {
  const { showSuccess, showError } = useNotification();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [measurementModal, setMeasurementModal] = useState({
    isOpen: false,
    customerId: null,
    customerName: '',
    existingMeasurements: null,
  });

  useEffect(() => {
    loadCustomers();
  }, [search]);

  const loadCustomers = async () => {
    try {
      const response = await customerAPI.getAll({ search });
      setCustomers(response.data.data || response.data);
    } catch (error) {
      showError('Error loading customers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await customerAPI.delete(id);
      loadCustomers();
    } catch (error) {
      showError('Error deleting customer');
    }
  };

  const handleOpenMeasurements = async (customer) => {
    try {
      const response = await measurementAPI.getLatestByCustomer(customer.id);
      setMeasurementModal({
        isOpen: true,
        customerId: customer.id,
        customerName: customer.name,
        existingMeasurements: response.data.data || null,
      });
    } catch (error) {
      console.error('Error loading measurements');
      setMeasurementModal({
        isOpen: true,
        customerId: customer.id,
        customerName: customer.name,
        existingMeasurements: null,
      });
    }
  };

  const handleCloseMeasurementModal = () => {
    setMeasurementModal({
      isOpen: false,
      customerId: null,
      customerName: '',
      existingMeasurements: null,
    });
  };

  const handleSaveMeasurements = async (measurements) => {
    try {
      const measurementData = {
        ...measurements,
        customer_id: measurementModal.customerId,
      };

      if (measurementModal.existingMeasurements) {
        await measurementAPI.update(measurementModal.existingMeasurements.id, measurementData);
        showSuccess('Measurements updated successfully!');
      } else {
        await measurementAPI.create(measurementData);
        showSuccess('Measurements saved successfully!');
      }
    } catch (error) {
      showError('Error saving measurements');
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = customers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(customers.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('customers.addCustomer')}</h2>
          <p className="text-gray-600 mt-1">{t('customers.subtitle')}</p>
        </div>
        <Link
          to="/customers/new"
          className="flex items-center px-4 py-2 bg-gradient-to-r bg-gray-900 text-white rounded-lg hover:bg-yellow-500  transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Customer
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder={t('customers.searchCustomer')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <svg className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Modern Card Layout */}
        <div className="space-y-3">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 rounded-lg text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-1">Status</div>
            <div className="col-span-3">Name</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Address</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          {/* Card Items */}
          {currentItems.map((customer) => (
            <div key={customer.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100">
              <div className="grid grid-cols-12 gap-4 px-6 py-4 items-center">
                {/* Status */}
                <div className="col-span-1 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>

                {/* Name with Avatar */}
                <div className="col-span-3 flex items-center gap-3">
                  <div className="h-10 w-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {customer.name.charAt(0)}
                  </div>
                  <div className="text-sm font-semibold text-gray-900">{customer.name}</div>
                </div>

                {/* Phone */}
                <div className="col-span-2">
                  <div className="text-sm text-gray-900">{customer.phone}</div>
                </div>

                {/* Email */}
                <div className="col-span-3">
                  <div className="text-sm text-gray-600 truncate">{customer.email || '-'}</div>
                </div>

                {/* Address */}
                <div className="col-span-2">
                  <div className="text-sm text-gray-600 truncate">{customer.address || '-'}</div>
                </div>

                {/* Actions */}
                <div className="col-span-1 flex items-center justify-end gap-2">
                  <Link
                    to={`/customers/${customer.id}`}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </Link>
                  <button
                    onClick={() => handleOpenMeasurements(customer)}
                    className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="Measurements"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(customer.id)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-4">
          <div className="text-sm text-gray-700">
            Show <span className="font-medium">{Math.min(indexOfFirstItem + 1, customers.length)}</span> of{' '}
            <span className="font-medium">{customers.length}</span> customers
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                onClick={() => paginate(index + 1)}
                className={`px-3 py-1 text-sm font-medium rounded-md ${
                  currentPage === index + 1
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {index + 1}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <span className="text-sm text-gray-700 ml-2">
              {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, customers.length)}
            </span>
          </div>
        </div>
      </div>

      {/* Measurement Modal */}
      <MeasurementModal
        isOpen={measurementModal.isOpen}
        onClose={handleCloseMeasurementModal}
        onSave={handleSaveMeasurements}
        itemName={`Customer: ${measurementModal.customerName}`}
        existingMeasurements={measurementModal.existingMeasurements}
        previousMeasurements={null}
      />
    </div>
  );
};

export default CustomerList;
